/**
 * Header scroll state
 *
 * In the first part of the page the masthead stays in normal flow and scrolls away.
 * After about half a screen it is pinned and slides down from the top.
 * Scrolling back above that point slides it away again.
 */

const PIN_AT_VIEWPORT = 0.45;
const FOLD_EXIT_BUFFER = 48;
const SLIDE_FALLBACK_MS = 700;

function initHeaderScrollBehavior() {
  const header = document.getElementById("masthead");

  if (!header) return;

  const spacer = document.createElement("div");
  spacer.className = "masthead-spacer";
  spacer.setAttribute("aria-hidden", "true");
  header.before(spacer);

  let exitTransitionHandler = null;
  let exitTimer = 0;
  let enterFrame = 0;

  function prefersReducedMotion() {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  }

  function scrollY() {
    return window.scrollY || document.documentElement.scrollTop || 0;
  }

  function pinThreshold() {
    return Math.round(window.innerHeight * PIN_AT_VIEWPORT);
  }

  function isPastFirstFold(currentlyPinned) {
    const fold = pinThreshold();
    const y = scrollY();

    if (currentlyPinned) {
      return y >= Math.max(0, fold - FOLD_EXIT_BUFFER);
    }

    return y >= fold;
  }

  function cancelEnter() {
    if (!enterFrame) return;
    cancelAnimationFrame(enterFrame);
    enterFrame = 0;
  }

  function removeExitListener() {
    if (exitTransitionHandler) {
      header.removeEventListener("transitionend", exitTransitionHandler);
      exitTransitionHandler = null;
    }

    if (exitTimer) {
      window.clearTimeout(exitTimer);
      exitTimer = 0;
    }
  }

  function holdSpace() {
    spacer.style.height = `${header.offsetHeight}px`;
    spacer.classList.add("is-active");
  }

  function releaseSpace() {
    spacer.classList.remove("is-active");
    spacer.style.height = "";
  }

  function unpin() {
    cancelEnter();
    removeExitListener();
    header.classList.remove("past-hero", "past-hero-exit", "is-entering");
    releaseSpace();
  }

  function pinHeader(animate) {
    if (header.classList.contains("past-hero-exit")) {
      removeExitListener();
      header.classList.remove("past-hero-exit", "is-entering");
      return;
    }

    if (header.classList.contains("past-hero")) {
      return;
    }

    holdSpace();
    header.classList.add("past-hero");

    if (!animate || prefersReducedMotion()) {
      return;
    }

    header.classList.add("is-entering");
    cancelEnter();
    enterFrame = requestAnimationFrame(() => {
      enterFrame = requestAnimationFrame(() => {
        enterFrame = 0;
        header.classList.remove("is-entering");
      });
    });
  }

  function finishExit() {
    const stillPinned =
      header.classList.contains("past-hero") ||
      header.classList.contains("past-hero-exit");

    if (!stillPinned) {
      removeExitListener();
      return;
    }

    if (isPastFirstFold(true)) {
      header.classList.remove("past-hero-exit");
      removeExitListener();
      return;
    }

    unpin();
  }

  function startExit() {
    if (header.classList.contains("past-hero-exit")) {
      return;
    }

    cancelEnter();

    if (prefersReducedMotion() || header.classList.contains("is-entering")) {
      unpin();
      return;
    }

    header.classList.remove("is-entering");
    header.classList.add("past-hero-exit");
    removeExitListener();

    exitTransitionHandler = (event) => {
      if (event.target !== header || event.propertyName !== "transform") {
        return;
      }
      finishExit();
    };

    header.addEventListener("transitionend", exitTransitionHandler);
    exitTimer = window.setTimeout(finishExit, SLIDE_FALLBACK_MS);
  }

  function updateHeaderState() {
    if (document.body.classList.contains("mobile-menu-active")) {
      return;
    }

    const pinned = header.classList.contains("past-hero");
    const past = isPastFirstFold(pinned);

    if (past) {
      pinHeader(!pinned);
      return;
    }

    if (!pinned) {
      return;
    }

    startExit();
  }

  function onScroll() {
    updateHeaderState();
  }

  function onResize() {
    if (header.classList.contains("past-hero")) {
      holdSpace();
    }
    updateHeaderState();
  }

  window.addEventListener("scroll", onScroll, { passive: true });
  window.addEventListener("resize", onResize);

  if (isPastFirstFold(false)) {
    pinHeader(false);
  } else {
    unpin();
  }
}

export { initHeaderScrollBehavior };
